import React, { useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { EContractAction } from "actions";

import { PDFPreview } from "components/BillingWorkFlow/PDFPreview";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Button, DeleteButton } from "components/Form";
import { Dialog, DialogActions, DialogTitle } from "components/MaterialDialog";

import { EContractParty } from "constants/eContract";
import { ModalAdapterProps, ModalTypes } from "constants/navigation";
import {
  DeploymentPermissions,
  EContractPermissions,
} from "constants/permissions";

import { getEContractCodeVersionAndStatusText } from "lib/econtracts";
import { getReportJobUrl } from "lib/navigation";
import { hasPermission } from "lib/permissions";
import toast from "lib/toast";

import { getActiveRole, getEContractById } from "selectors";

import history from "appHistory";
import {
  useBoolean,
  useHasDeploymentPermission,
  useHasPermission,
  useIsMobile,
  useModalAdapter,
} from "hooks";

import { EContractSendEmailModal } from "./sendEmailModal";

interface EContractModalParams {
  id: string;
}

export const EContractModal = (
  props: ModalAdapterProps,
): React.JSX.Element | null => {
  const [hashParams, returnTo, onClose] = useModalAdapter<EContractModalParams>(
    ModalTypes.EContract,
    props,
  );
  const { id: eContractId } = hashParams;

  const dispatch = useDispatch();
  const { slug: activeRoleSlug } = useSelector(getActiveRole);
  const eContract = useSelector(getEContractById(eContractId));

  const fullScreen = useIsMobile();

  const hasFeaturePermission = useHasDeploymentPermission(
    DeploymentPermissions.featureEContracts,
  );
  const hasReadPermission = useHasPermission(
    getEContractById(eContractId),
    EContractPermissions.read,
  );
  const canVoid = hasPermission(eContract, EContractPermissions.void);
  const canAccept = hasPermission(eContract, EContractPermissions.accept);

  const [isVoidDialogShowing, showVoidDialog, hideVoidDialog] =
    useBoolean(false);

  const [emailDialogParty, setEmailDialogParty] = useState(null);

  if (!hasFeaturePermission || !hasReadPermission) {
    // don't display model if feature permission missing
    toast.error("You are unable to view this eContract");
    if (returnTo) {
      history.push(returnTo);
    }
    return null;
  }

  const voidEContract = () => {
    dispatch(EContractAction.void({ id: eContract.id }));
    hideVoidDialog();
  };

  const { voidedOn, agentAcceptedOn } = eContract;
  const eContractStatus = getEContractCodeVersionAndStatusText(eContract);

  // add status onto url for force reloading pdf view when econtract status changes.
  // status query param is not used by backend, and url is otherwise static
  // adding this allows the PDF to refresh when voided, without closing and re-opening the modal.
  const documentPreviewUrl = `${getReportJobUrl(
    eContract.documentId,
    activeRoleSlug,
  )}&status=${eContractStatus}`;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      height="100%"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle onClose={onClose}>eContract - {eContractStatus}</DialogTitle>
      <PDFPreview url={documentPreviewUrl} />
      <DialogActions>
        <DeleteButton disabled={voidedOn || !canVoid} onClick={showVoidDialog}>
          Void eContract
        </DeleteButton>
        <Button
          disabled={!canAccept}
          onClick={() => setEmailDialogParty(EContractParty.BUYER)}
        >
          {agentAcceptedOn || voidedOn ? "" : "Accept and "} Send to Buyer
        </Button>
        <Button
          disabled={!canAccept}
          onClick={() => setEmailDialogParty(EContractParty.VENDOR)}
        >
          {agentAcceptedOn || voidedOn ? "" : "Accept and "} Send to Vendor
        </Button>
      </DialogActions>
      {isVoidDialogShowing ? (
        <ConfirmDialog
          buttonMessage="Void"
          isOpen={isVoidDialogShowing}
          onCancel={hideVoidDialog}
          onDelete={voidEContract}
          title="Confirm you want to void this eContract"
        />
      ) : null}
      {emailDialogParty ? (
        <EContractSendEmailModal
          eContractId={eContractId}
          onClose={() => setEmailDialogParty(null)}
          party={emailDialogParty}
        />
      ) : null}
    </Dialog>
  );
};
